import React, { useEffect, useState } from 'react';
import { IconButton, Tooltip } from '@mui/material';
import { Settings as SettingsIcon } from '@mui/icons-material';
import { makeStyles } from 'tss-react/mui';
import { IconButton as OverlayIconButton } from '@ui/buttons';
import { Menu } from '@ui/menus/Menu';
import { DesignMap } from '../map';
import {
  DESIGN_COLLAPSED_TABS_INSET_VAR,
  DESIGN_RIGHT_PANEL_WIDTH_VAR,
} from '../design-right-panel-resize.hook';
import type { BlueprintUnderlay } from '@shared/models/design/blueprint-underlay.model';
import {
  useSetShowBackgroundMap,
  useSetShowBlueprintUnderlay,
  useSetShowNearbyBuildings,
  useShowBackgroundMap,
  useShowBlueprintUnderlay,
  useShowNearbyBuildings,
} from '../store/design-store.hooks';

type MapViewSettingsProps = {
  map: DesignMap | undefined;
  blueprintUnderlay?: BlueprintUnderlay;
  /** Select / deselect the underlay for move, scale and page actions. */
  blueprintSelect?: {
    selected: boolean;
    onToggle: () => void;
  };
};

export const MapViewSettings: React.FC<MapViewSettingsProps> = ({
  map,
  blueprintUnderlay,
  blueprintSelect,
}) => {
  const { classes } = useStyles();
  const [menuOpen, setMenuOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const showBackgroundMap = useShowBackgroundMap();
  const showNearbyBuildings = useShowNearbyBuildings();
  const showBlueprintUnderlay = useShowBlueprintUnderlay();
  const setShowBackgroundMap = useSetShowBackgroundMap();
  const setShowNearbyBuildings = useSetShowNearbyBuildings();
  const setShowBlueprintUnderlay = useSetShowBlueprintUnderlay();

  // Re-apply persisted toggles whenever the map remounts (e.g. Design ↔ Build).
  useEffect(() => {
    if (!map) return;
    map.setShowBackgroundMap(showBackgroundMap);
    map.setShowNearbyBuildings(showBackgroundMap ? showNearbyBuildings : false);
  }, [map, showBackgroundMap, showNearbyBuildings]);

  const handleToggleBackgroundMap = (next: boolean) => {
    setShowBackgroundMap(next);
    map?.setShowBackgroundMap(next);
    // When background map is hidden, force nearby buildings off too.
    // When re-enabled, restore the persisted checkbox value.
    map?.setShowNearbyBuildings(next ? showNearbyBuildings : false);
  };

  const handleToggleNearbyBuildings = (next: boolean) => {
    setShowNearbyBuildings(next);
    map?.setShowNearbyBuildings(next);
  };

  const handleToggleBlueprint = (next: boolean) => {
    if (!blueprintUnderlay) return;
    setShowBlueprintUnderlay(next);
  };

  const handleToggleMenu = (event: React.MouseEvent<HTMLElement>) => {
    if (menuOpen) {
      setMenuOpen(false);
      setAnchorEl(null);
      return;
    }
    setAnchorEl(event.currentTarget);
    setMenuOpen(true);
  };

  const handleMenuClose = () => {
    setMenuOpen(false);
    setAnchorEl(null);
  };

  return (
    <div className={classes.root}>
      <Tooltip title="View settings" placement="left">
        <IconButton
          className={classes.iconButton}
          onClick={handleToggleMenu}
          size="small"
          aria-label="View settings"
        >
          <SettingsIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu
        open={menuOpen}
        anchorEl={anchorEl}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Menu.Group label="Show">
          <Menu.CheckboxItem
            checked={showBackgroundMap}
            onChange={handleToggleBackgroundMap}
          >
            Background map
          </Menu.CheckboxItem>
          <Menu.CheckboxItem
            checked={showNearbyBuildings}
            onChange={handleToggleNearbyBuildings}
            disabled={!showBackgroundMap}
          >
            Nearby buildings
          </Menu.CheckboxItem>
          <Menu.CheckboxItem
            checked={showBlueprintUnderlay}
            onChange={handleToggleBlueprint}
            disabled={!blueprintUnderlay}
          >
            Underlay
            {blueprintSelect && (
              <Menu.Item.Action>
                <Tooltip title="Edit">
                  <OverlayIconButton
                    category="mui"
                    icon="Edit"
                    size="small"
                    shape="square"
                    aria-label={
                      blueprintSelect.selected ? 'Deselect PDF' : 'Edit PDF'
                    }
                    aria-pressed={blueprintSelect.selected}
                    onClick={() => {
                      handleMenuClose();
                      blueprintSelect.onToggle();
                    }}
                  />
                </Tooltip>
              </Menu.Item.Action>
            )}
          </Menu.CheckboxItem>
        </Menu.Group>
      </Menu>
    </div>
  );
};

const useStyles = makeStyles()(() => ({
  root: {
    // Between the search icon and the north mark, centred on the mark.
    position: 'absolute',
    top: 30,
    right: `calc(64px + var(${DESIGN_RIGHT_PANEL_WIDTH_VAR}, 0px) + var(${DESIGN_COLLAPSED_TABS_INSET_VAR}, 0px))`,
    transform: 'translateY(-50%)',
    zIndex: 3,
  },
  iconButton: {
    backgroundColor: 'transparent',
    boxShadow: 'none',
    color: 'rgba(0, 0, 0, 0.7)',
    transition: 'color 0.15s ease',
    '&:hover': {
      backgroundColor: 'transparent',
      color: 'rgba(0, 0, 0, 1)',
    },
  },
}));
